import { FormBlockInstance, LocalizedText } from '../api-types';

export const STATEMENT_TYPE = 'statement';
export const STATEMENT_TYPE_VERSION = 1;

export interface StatementParagraph {
  key: string;
  label: LocalizedText | null;
}

export interface StatementConfig {
  paragraphs: StatementParagraph[];
  emphasis: boolean;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asLocalizedText = (value: unknown): LocalizedText | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  );
  return entries.length ? Object.fromEntries(entries) : null;
};

/**
 * Read a stored statement config into the shape this renderer needs.
 *
 * Tolerant for the same reason its single-select sibling is: the definition was normalized and
 * validated by pro-core before it was stored, so the fields are expected — but a renderer that
 * throws on a surprise takes the whole form down for the respondent, and a statement showing one
 * paragraph fewer than intended is a far smaller failure than a blank screen.
 */
export const readStatementConfig = (
  block: FormBlockInstance
): StatementConfig => {
  const config = asRecord(block.config);
  const data = asRecord(config.data);
  const presentation = asRecord(config.presentation);
  const rawParagraphs = Array.isArray(data.paragraphs) ? data.paragraphs : [];

  return {
    paragraphs: rawParagraphs
      .map(asRecord)
      .filter(
        paragraph =>
          typeof paragraph.key === 'string' && paragraph.key.length > 0
      )
      .map(paragraph => ({
        key: paragraph.key as string,
        label: asLocalizedText(paragraph.label)
      })),
    emphasis: presentation.emphasis === true
  };
};
